import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getActiveRenders, type ActiveRender } from '../../services/studioService';

/**
 * RENDER TRACKER — the one piece of the Studio that follows the creator around the app.
 *
 * A render takes 20–40 minutes. Nobody sits and watches it, so the moment they navigate away every signal
 * disappeared and the honest read of the product became "did that work, or did I waste my credits?".
 * Higgsfield ships an entire separate Gen Tracker dashboard for this exact reason, plus a help-centre page for
 * generations that get stuck — long jobs need a persistent indicator and a way back.
 *
 * This is the small version of that: a chip that appears only while something is actually rendering, says how
 * far along it is, and takes you back to the production when clicked (which is only possible because a
 * production now has its own address, /dashboard/{slug}).
 *
 * POLLING IS THE COST HERE, so it is deliberately adaptive: slow while nothing is happening, quicker while a
 * render is live, and stopped entirely while the tab is hidden. A fixed fast poll from every screen would be
 * a standing read cost on every user for a thing that is usually idle.
 */
const IDLE_MS = 90_000;    // nothing rendering: just often enough to notice one started elsewhere
const ACTIVE_MS = 20_000;  // something rendering: often enough that the count visibly moves

interface Props {
  className?: string;
  /**
   * The FOLDED-PANE form: just the spinner, sized like the rail's other control. A render outlives the pane
   * it was started from, so collapsing the explorer must not make a 20-40 minute job invisible (screenshot79's
   * rule, applied to the rail). Same component, same single poll — only the shape of the chip changes.
   */
  dot?: boolean;
}

const RenderTracker: React.FC<Props> = ({ className = '', dot = false }) => {
  const [renders, setRenders] = useState<ActiveRender[]>([]);
  const navigate = useNavigate();
  const timer = useRef<number | null>(null);
  // The latest list, readable outside a state updater: the cadence is decided from this, never inside setState.
  const latest = useRef<ActiveRender[]>([]);

  const poll = useCallback(async () => {
    // A hidden tab does not need to know; polling it is pure waste and drains a laptop battery.
    if (typeof document !== 'undefined' && document.hidden) return;
    try {
      const next = await getActiveRenders();
      latest.current = next;
      setRenders(next);
    } catch {
      /* Not signed in, offline, or the route is unavailable — the tracker is ambient, never an error surface. */
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      await poll();
      if (!alive) return;
      /*
       * EXACTLY ONE TIMER PER TICK (BUGS.md 2026-10-04). This used to be armed inside a setState updater, which
       * React may run more than once, so every tick armed two timers, those armed four, and a render page made
       * tens of thousands of calls. The cadence now follows a ref, outside any updater.
       */
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(tick, latest.current.length ? ACTIVE_MS : IDLE_MS);
    };
    void tick();
    // Coming back to the tab should refresh immediately rather than waiting out the remaining interval.
    const onVisible = () => { if (!document.hidden) void poll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      if (timer.current) window.clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [poll]);

  if (!renders.length) return null;
  const r = renders[0];
  const more = renders.length - 1;
  const label = `${r.title || 'Untitled episode'} — rendering ${r.done} of ${r.total}${more > 0 ? ` (+${more} more)` : ''}`;

  if (dot) {
    return (
      <button
        type="button"
        data-testid="render-tracker-dot"
        onClick={() => navigate(`/dashboard/${r.slug}`)}
        title={label}
        aria-label={label}
        className={`rounded-lg p-1.5 text-secondary-500 transition-colors hover:bg-secondary-100
                    hover:text-secondary-900 dark:hover:bg-secondary-800 dark:hover:text-white ${className}`}
      >
        <Loader2 className="h-5 w-5 animate-spin" />
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid="render-tracker"
      onClick={() => navigate(`/dashboard/${r.slug}`)}
      title={label}
      className={`flex w-full items-center gap-2 rounded-lg border border-secondary-200 bg-white px-2.5 py-2
                  text-left transition-colors hover:border-secondary-400
                  dark:border-secondary-700 dark:bg-secondary-900 dark:hover:border-secondary-500 ${className}`}
    >
      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-secondary-500" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[11px] font-medium text-secondary-900 dark:text-white">
          {r.title || 'Untitled episode'}
        </span>
        <span className="block text-[11px] text-secondary-500 dark:text-secondary-400">
          Rendering {r.done} of {r.total}
          {more > 0 && ` · +${more} more`}
        </span>
      </span>
    </button>
  );
};

export default RenderTracker;
